import axios from '../axios'
import { COURSEAIROUTES } from 'src/router/Api'

export type CourseAiSkill =
	| 'writing'
	| 'dictation'
	| 'pronunciation'
	| 'speaking'
	| 'combo'

export type CourseAiTopicVisibility = 'public' | 'invisible'

export interface CourseAiCourse {
	id: string
	name?: string | null
	title?: string | null
	type?: string | null
	skill?: string | null
	slug?: string | null
	[key: string]: any
}

export interface CourseAiTopicStat {
	total_lession?: number
	public?: number
	invisible?: number
	leaners_count?: number
	last_updated?: string | null
	[key: string]: any
}

export interface CourseAiTopic {
	id: string
	course_id?: string
	title?: string | null
	level?: string | null
	topic?: string | null
	original_text?: string | null
	audio_url?: string | null
	day?: number | null
	day_index?: number | null
	order?: number | null
	visibility?: CourseAiTopicVisibility | string | null
	is_active?: boolean | null
	is_public?: boolean | null
	learners_count?: number | null
	leaners_count?: number | null
	updated_at?: string | null
	created_at?: string | null
	deleted_at?: string | null
	[key: string]: any
}

export interface WritingTopicPayload {
	course_id: string
	title: string
	level: string
	topic: string
	visibility?: string
	is_active?: boolean
}

export interface DictationTopicPayload {
	course_id: string
	title: string
	level: string
	topic: string
	audio_url?: string
	original_text?: string
	visibility?: string
	is_active?: boolean
}

export interface WritingTopicUpdatePayload {
	title?: string
	level?: string
	topic?: string
	visibility?: string
	is_active?: boolean
}

export interface DictationTopicUpdatePayload {
	title?: string
	level?: string
	topic?: string
	audio_url?: string
	original_text?: string
	visibility?: string
	is_active?: boolean
}

const asArray = (value: any): any[] => {
	if (Array.isArray(value)) return value
	if (Array.isArray(value?.rows)) return value.rows
	if (Array.isArray(value?.data)) return value.data
	if (Array.isArray(value?.items)) return value.items
	return []
}

export const unwrapList = (response: any): { rows: any[]; count: number } => {
	const results = response?.data?.results
	let rows: any[] = []
	if (Array.isArray(results?.objects?.rows)) rows = results.objects.rows
	else if (Array.isArray(results?.object?.rows)) rows = results.object.rows
	else if (Array.isArray(results?.objects)) rows = results.objects
	else if (Array.isArray(results?.object)) rows = results.object
	else if (Array.isArray(results)) rows = results
	else rows = asArray(results?.objects ?? results?.object)

	const count =
		results?.objects?.count ?? results?.object?.count ?? rows.length
	return { rows, count: Number(count) || 0 }
}

export const unwrapObject = <T = any>(response: any): T | null => {
	const results = response?.data?.results
	return (results?.object ?? results?.objects ?? results ?? null) as T | null
}

export const getCourseAiList = async (params?: Record<string, any>) =>
	axios.get(COURSEAIROUTES.list, { params })

export const getCourseAiDetail = async (id: string) =>
	axios.get(COURSEAIROUTES.detail(id))

export const getCourseAiTopicStat = async (courseId: string) =>
	axios.get(COURSEAIROUTES.topicStat, { params: { course_id: courseId } })

export const getWritingTopics = async (params?: { course_id?: string }) =>
	axios.get(COURSEAIROUTES.writingTopics, {
		params: {
			...params,
			where: JSON.stringify({ deleted_at: { $is: null } }),
		},
	})

export const createWritingTopic = async (body: WritingTopicPayload) =>
	axios.post(COURSEAIROUTES.writingTopics, body)

export const updateWritingTopic = async (
	id: string,
	body: WritingTopicUpdatePayload,
) => axios.put(COURSEAIROUTES.writingTopic(id), body)

export const deleteWritingTopic = async (id: string) =>
	axios.delete(COURSEAIROUTES.writingTopic(id))

/** List CMS lesson prototypes (not daily instance topics). */
export const getDictationTopics = async (params?: { course_id?: string }) =>
	axios.get(COURSEAIROUTES.dictationTopicPrototypes, {
		params: {
			...params,
			where: JSON.stringify({ deleted_at: { $is: null } }),
		},
	})

export const createDictationTopic = async (body: DictationTopicPayload) =>
	axios.post(COURSEAIROUTES.dictationTopicPrototypes, body)

export const updateDictationTopic = async (
	id: string,
	body: DictationTopicUpdatePayload,
) => axios.put(COURSEAIROUTES.dictationTopic(id), body)

export const deleteDictationTopic = async (id: string) =>
	axios.delete(COURSEAIROUTES.dictationTopic(id))
